import { useCallback, useMemo, useState } from 'react';
import { Stripe } from '@stripe/stripe-js';
import pRetry from 'p-retry';
import appConfig from '@frontend/env';
import { i18next } from '@frontend/i18n';
import { paymentsSentry } from '@frontend/payments-hooks';
import { useAlert } from '@frontend/design-system';
import { useMounted } from './use-mounted';

type StripePromise = Promise<Stripe | null>;

const STRIPE_KEY = appConfig.STRIPE_EXPRESS_API_KEY;

export const STRIPE_LOADING_FAILED_MSG = 'Failed to load Stripe.js within maximum retries.';
const STRIPE_UI_ERROR_MSG = 'Failed to load Stripe.';
const stripeErrorMessage = i18next.t(STRIPE_UI_ERROR_MSG, { ns: 'payments' });

const stripePromiseCache = new Map<string, StripePromise>();

interface UseStripePromiseOptions {
  stripeExpressKey?: string;
}

/**
 * @deprecated "isLive" and "StripeKey" never change.
 * We don't need a key cache or most of this.
 */
export const useStripePromise = ({ stripeExpressKey: stripeKey = STRIPE_KEY }: UseStripePromiseOptions = {}) => {
  const [error, setError] = useState<unknown>();
  const [isLoading, setIsLoading] = useState(false);
  const { runIfMounted } = useMounted();
  const alerts = useAlert();

  const loadStripeSdk = useCallback(
    async (stripeKey: string): StripePromise => {
      runIfMounted(() => setIsLoading(true));
      try {
        const { loadStripe } = await import('@stripe/stripe-js');
        const stripe = await pRetry(() => loadStripe(stripeKey), { retries: 3 });
        runIfMounted(() => setIsLoading(false));
        return stripe;
      } catch (error) {
        stripePromiseCache.delete(stripeKey);

        paymentsSentry.error(
          STRIPE_LOADING_FAILED_MSG,
          'useStripePromise',
          `Error: ${error instanceof Error ? error.message : JSON.stringify(error)}`
        );
        console.error(STRIPE_LOADING_FAILED_MSG, error);
        runIfMounted(() => {
          setError(error);
          setIsLoading(false);
        });

        return null;
      }
    },
    [runIfMounted]
  );

  // Use useMemo to get or create the singleton promise
  const stripePromise = useMemo(() => {
    const existingPromise = stripePromiseCache.get(stripeKey);
    const promise = stripeKey ? existingPromise || loadStripeSdk(stripeKey) : Promise.resolve(null);
    if (!existingPromise) stripePromiseCache.set(stripeKey, promise);
    return promise;
  }, [stripeKey, loadStripeSdk, runIfMounted]);

  const alertStripeError = useCallback(() => {
    if (error) {
      alerts.error(stripeErrorMessage);
    }
  }, [error]);

  return { stripePromise, stripeKey, error, isLoading, alertStripeError };
};

// Used in unit testing
export const clearStripePromiseCache = () => {
  stripePromiseCache.clear();
};
